/**
 * MODULE: _shared/frontend-shell/src/components/GlobalSearch.tsx
 * PURPOSE: Global search command palette — pages, Sales Hub content (templates API), deals, accounts, CRM contacts
 * DEPENDENCIES: react-router-dom, @heroicons/react, @domain/sales-opportunities, ../features/crm/hooks/useCrmContacts, design-system CommandPalette
 * SIDE_EFFECTS: Navigation; debounced network search for catalogue content
 * AGENT_SAFETY: 🟢 SAFE
 * Last updated: 2026-05-11 — CRM contact hits from **`useCrmContacts`** + **`/crm/contacts/:id`**; palette mounts only when open (no background CRM list fetch).
 */

import type { ReactNode } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  UserIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';
import type { Opportunity } from '@domain/sales-opportunities/contracts/types';
import { useGoldenCrmOpportunities } from '../hooks/useGoldenCrmOpportunities';
import type { ManagedContact } from '@shared/lib/frontend/api/contactApi';
import { useCrmContacts } from '../features/crm/hooks/useCrmContacts';
import { buildCompanyDetailPath } from '../routes/builders';
import { createCommandPaletteProviders } from '../design-system/CommandPalette';

const DEBOUNCE_MS = 200;

type SearchResultType = 'account' | 'opportunity' | 'contact' | 'page' | 'content';

interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  path: string;
  value?: number;
  icon?: ReactNode;
  onSelect?: () => void;
}

const QUICK_NAV: SearchResult[] = [
  { id: 'langflow-assistant', type: 'page', title: 'Assistant', path: '/langflow-assistant' },
  { id: 'crm', type: 'page', title: 'CRM', path: '/crm' },
  { id: 'territory', type: 'page', title: 'Territory', path: '/territory' },
  { id: 'accounts', type: 'page', title: 'Accounts', path: '/accounts' },
  { id: 'deals', type: 'page', title: 'Deals', path: '/deals' },
  { id: 'opportunities', type: 'page', title: 'Deals', path: '/crm/deals' },
  { id: 'my-quota', type: 'page', title: 'My quota', path: '/crm/my-quota' },
  { id: 'financial', type: 'page', title: 'Sales performance', path: '/financial' },
  { id: 'pipe-gen', type: 'page', title: 'Pipeline Generation', path: '/pipeline-generation' },
  { id: 'sales-hub', type: 'page', title: 'Sales Hub', path: '/sales-hub' },
  { id: 'sales-profile', type: 'page', title: 'My Company', path: '/sales-profile' },
  { id: 'products', type: 'page', title: 'My Products', path: '/products' },
  { id: 'guides', type: 'page', title: 'My Sales Guides', path: '/sales-guides' },
  { id: 'activities', type: 'page', title: 'Activity', path: '/activities' },
  { id: 'settings', type: 'page', title: 'Settings', path: '/settings' },
  { id: 'settings-integrations', type: 'page', title: 'Integrations', path: '/settings?tab=integrations' },
  { id: 'settings-email-accounts', type: 'page', title: 'Email accounts', path: '/settings/email-accounts' },
  { id: 'settings-ai-usage', type: 'page', title: 'AI usage', path: '/settings/ai-usage' },
  { id: 'settings-billing', type: 'page', title: 'Billing & plans', path: '/settings/billing' },
];

const GROUP_ORDER: SearchResultType[] = ['page', 'content', 'opportunity', 'account', 'contact'];

const EMPTY_COPY: Record<SearchResultType, string> = {
  page: 'No matching pages',
  content: 'No matching content',
  opportunity: 'No matching deals',
  account: 'No matching accounts',
  contact: 'No matching contacts',
};

function useDebouncedValue(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function getResultIcon(result: SearchResult) {
  if (result.icon) return result.icon;
  switch (result.type) {
    case 'opportunity':
      return <CurrencyDollarIcon className="w-4 h-4 text-green-500" />;
    case 'account':
      return <BuildingOfficeIcon className="w-4 h-4 text-blue-500" />;
    case 'contact':
      return <UserIcon className="w-4 h-4 text-purple-500" />;
    case 'content':
      return <DocumentIcon className="w-4 h-4 text-orange-500" />;
    case 'page':
    default:
      return <DocumentIcon className="w-4 h-4 text-gray-400" />;
  }
}

function getResultTypeLabel(type: SearchResultType): string {
  switch (type) {
    case 'opportunity':
      return 'Deal';
    case 'account':
      return 'Account';
    case 'contact':
      return 'Contact';
    case 'content':
      return 'Content';
    case 'page':
    default:
      return 'Page';
  }
}

function getGroupHeading(type: SearchResultType): string {
  switch (type) {
    case 'page':
      return 'Pages';
    case 'content':
      return 'Content';
    case 'opportunity':
      return 'Deals';
    case 'account':
      return 'Accounts';
    case 'contact':
      return 'Contacts';
    default:
      return type;
  }
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

/** CRM list fetch lives here so opening ⌘⇧K does not run `useCrmContacts` on every AppLayout render while closed. */
export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  if (!isOpen) return null;
  return <GlobalSearchPalette onClose={onClose} />;
}

function contactDisplayName(c: ManagedContact): string {
  const n = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
  return n || c.email?.trim() || 'Contact';
}

function GlobalSearchPalette({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [contentResults, setContentResults] = useState<SearchResult[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const navigate = useNavigate();
  const { data: opportunities = [] } = useGoldenCrmOpportunities();
  const { data: crmContacts = [] } = useCrmContacts();

  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);

  const paletteProviders = useMemo(() => createCommandPaletteProviders(navigate), [navigate]);
  const contentProvider = paletteProviders[0]!;

  const hasActiveSearch = query.trim().length > 0;

  useEffect(() => {
    setQuery('');
    setSelectedIndex(0);
    setContentResults([]);
    setContentLoading(false);
  }, []);

  useEffect(() => {
    const t = debouncedQuery.trim();
    if (t.length < (contentProvider.minQueryLength ?? 2)) {
      setContentResults([]);
      setContentLoading(false);
      return;
    }

    const ac = new AbortController();
    let cancelled = false;
    setContentLoading(true);

    void (async () => {
      try {
        const items = await contentProvider.search(t, { signal: ac.signal });
        if (cancelled) return;
        setContentResults(
          items.map((it) => ({
            id: it.id,
            type: 'content' as const,
            title: it.label,
            subtitle: it.sublabel,
            path: '',
            icon: it.icon,
            onSelect: it.onSelect,
          }))
        );
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === 'AbortError' || ac.signal.aborted) return;
        if (!cancelled) setContentResults([]);
      } finally {
        if (!cancelled) setContentLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [debouncedQuery, contentProvider]);

  const clientResults = useMemo(() => {
    if (!hasActiveSearch) {
      return { page: QUICK_NAV.slice(0, 8), opportunity: [], account: [], contact: [] as SearchResult[] };
    }

    const q = query.toLowerCase().trim();

    const pageResults = QUICK_NAV.filter(
      (r) =>
        r.title.toLowerCase().includes(q) || (r.subtitle && r.subtitle.toLowerCase().includes(q))
    );

    const dealResults: SearchResult[] = opportunities
      .filter(
        (opp: Opportunity) =>
          opp.name?.toLowerCase().includes(q) || opp.account_name?.toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map((opp: Opportunity) => ({
        id: `deal-${opp.id}`,
        type: 'opportunity' as const,
        title: opp.name || 'Unnamed Deal',
        subtitle: `${opp.stage?.replace(/_/g, ' ')} · $${(opp.value || 0).toLocaleString()}`,
        path: `/crm/deals/${encodeURIComponent(opp.id)}`,
        value: opp.value,
      }));

    const accountNames = new Set<string>();
    opportunities.forEach((opp: Opportunity) => {
      if (opp.account_name && opp.account_name.toLowerCase().includes(q)) {
        accountNames.add(opp.account_name);
      }
    });

    const accountResults: SearchResult[] = Array.from(accountNames)
      .slice(0, 5)
      .map((name) => ({
        id: `account-${name}`,
        type: 'account' as const,
        title: name,
        subtitle: 'Company',
        path: buildCompanyDetailPath(name),
      }));

    const directContacts = crmContacts
      .filter((c) => {
        const name = contactDisplayName(c).toLowerCase();
        const email = c.email?.toLowerCase() ?? '';
        const company = c.company?.toLowerCase() ?? '';
        return name.includes(q) || email.includes(q) || company.includes(q);
      })
      .slice(0, 8);

    const contactResults: SearchResult[] = directContacts.map((c) => {
      const title = contactDisplayName(c);
      return {
        id: `contact-${c.id}`,
        type: 'contact' as const,
        title,
        subtitle: c.company ? `at ${c.company}` : c.email,
        path: `/crm/contacts/${c.id}`,
      };
    });

    return { page: pageResults, opportunity: dealResults, account: accountResults, contact: contactResults };
  }, [crmContacts, hasActiveSearch, query, opportunities]);

  const groupedResults = useMemo(() => {
    const groups: Record<SearchResultType, SearchResult[]> = {
      page: clientResults.page,
      content: contentResults,
      opportunity: clientResults.opportunity,
      account: clientResults.account,
      contact: clientResults.contact,
    };
    return groups;
  }, [clientResults, contentResults]);

  const flatOrderedResults = useMemo(() => {
    if (!hasActiveSearch) {
      return groupedResults.page;
    }
    const out: SearchResult[] = [];
    for (const t of GROUP_ORDER) {
      out.push(...groupedResults[t]);
    }
    return out;
  }, [hasActiveSearch, groupedResults]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    setSelectedIndex((i) => Math.min(i, Math.max(0, flatOrderedResults.length - 1)));
  }, [flatOrderedResults.length]);

  const closePalette = useCallback(() => {
    setQuery('');
    setSelectedIndex(0);
    setContentResults([]);
    onClose();
  }, [onClose]);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      if (result.onSelect) {
        result.onSelect();
      } else {
        navigate(result.path);
      }
      closePalette();
    },
    [navigate, closePalette]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePalette();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, Math.max(0, flatOrderedResults.length - 1)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' && flatOrderedResults[selectedIndex]) {
        e.preventDefault();
        handleSelect(flatOrderedResults[selectedIndex]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closePalette, flatOrderedResults, selectedIndex, handleSelect]);

  const flatIndexByKey = useMemo(() => {
    const m = new Map<string, number>();
    flatOrderedResults.forEach((r, i) => {
      m.set(`${r.type}:${r.id}`, i);
    });
    return m;
  }, [flatOrderedResults]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] bg-black/50"
      onClick={closePalette}
      role="presentation"
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Global search"
      >
        <div className="flex items-center border-b border-gray-200 dark:border-gray-700 px-4">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search content, deals, accounts, contacts, pages..."
            className="flex-1 py-3 px-3 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
            autoFocus
          />
          <kbd className="hidden sm:inline px-2 py-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
            ESC
          </kbd>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {!hasActiveSearch ? (
            <>
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Pages
              </div>
              {flatOrderedResults.map((result, currentIndex) => (
                <button
                  key={result.id}
                  type="button"
                  onClick={() => handleSelect(result)}
                  onMouseEnter={() => setSelectedIndex(currentIndex)}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${
                    currentIndex === selectedIndex
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {getResultIcon(result)}
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{result.title}</span>
                    {result.subtitle && (
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 truncate">
                        {result.subtitle}
                      </span>
                    )}
                  </div>
                  <span className="ml-auto text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                    {getResultTypeLabel(result.type)}
                  </span>
                </button>
              ))}
            </>
          ) : (
            <>
              {GROUP_ORDER.map((type) => {
                const typeResults = groupedResults[type];
                const isContent = type === 'content';
                const minLen = contentProvider.minQueryLength ?? 2;
                const debouncedOk = debouncedQuery.trim().length >= minLen;
                const showContentRows = isContent && debouncedOk && !contentLoading;
                const showContentHint = isContent && !debouncedOk && !contentLoading;
                const showContentEmpty =
                  isContent && debouncedOk && !contentLoading && typeResults.length === 0;

                return (
                  <div key={type}>
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {getGroupHeading(type)}
                    </div>
                    {isContent && contentLoading && (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">Searching content…</div>
                    )}
                    {showContentHint && (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        Type at least 2 characters to search catalogue
                      </div>
                    )}
                    {showContentRows &&
                      typeResults.map((result) => {
                        const currentIndex = flatIndexByKey.get(`${result.type}:${result.id}`) ?? 0;
                        return (
                          <button
                            key={result.id}
                            type="button"
                            onClick={() => handleSelect(result)}
                            onMouseEnter={() => setSelectedIndex(currentIndex)}
                            className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${
                              currentIndex === selectedIndex
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                          >
                            {getResultIcon(result)}
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{result.title}</span>
                              {result.subtitle && (
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {result.subtitle}
                                </span>
                              )}
                            </div>
                            <span className="ml-auto text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                              {getResultTypeLabel(result.type)}
                            </span>
                          </button>
                        );
                      })}
                    {!isContent &&
                      (typeResults.length > 0 ? (
                        typeResults.map((result) => {
                          const currentIndex = flatIndexByKey.get(`${result.type}:${result.id}`) ?? 0;
                          return (
                            <button
                              key={result.id}
                              type="button"
                              onClick={() => handleSelect(result)}
                              onMouseEnter={() => setSelectedIndex(currentIndex)}
                              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${
                                currentIndex === selectedIndex
                                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              {getResultIcon(result)}
                              <div className="flex-1 min-w-0">
                                <span className="font-medium">{result.title}</span>
                                {result.subtitle && (
                                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {result.subtitle}
                                  </span>
                                )}
                              </div>
                              <span className="ml-auto text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                {getResultTypeLabel(result.type)}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {EMPTY_COPY[type]}
                        </div>
                      ))}
                    {showContentEmpty && (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{EMPTY_COPY.content}</div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
          <div className="flex gap-4">
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>⌘⇧K Toggle</span>
          </div>
          {query && (
            <span className="text-gray-400">
              {flatOrderedResults.length} result{flatOrderedResults.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default GlobalSearch;
